import React from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Users, Plus, MessageCircle, ArrowUpRight } from "lucide-react";
import { usePackage, useSite } from "@/lib/api";
import Seo from "@/components/common/Seo";
import NotFound from "@/components/common/NotFound";
import { useOrderList } from "@/context/OrderListContext";

const SITE = process.env.REACT_APP_SITE_URL;

export default function PackageDetail() {
  const { slug } = useParams();
  const { data: pkg, isLoading } = usePackage(slug);
  const { data: site } = useSite();
  const { addPackage } = useOrderList();

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-32 text-center font-mono text-sm text-charcoal/50" data-testid="package-loading">
        Paket yükleniyor...
      </div>
    );
  }
  if (!pkg) return <NotFound />;

  const handleAdd = () => {
    addPackage(pkg);
    toast.success(`${pkg.name} listene eklendi`, {
      description: `${pkg.items.length} ürün sipariş listene aktarıldı.`,
    });
  };

  const askUrl = site?.whatsapp
    ? `https://wa.me/${site.whatsapp}?text=${encodeURIComponent(
        `Merhaba ${site.brand || "Ertan Kasap"}, "${pkg.name}" paketi hakkında bilgi almak istiyorum.`
      )}`
    : "#";
  const origin = SITE || (typeof window !== "undefined" ? window.location.origin : "");

  return (
    <div data-testid="package-detail-page" className="bg-cream pb-24">
      <Seo
        title={pkg.name}
        description={pkg.description || `${pkg.name} — ${pkg.serves}. Sofranızı tek tıkla kurun, WhatsApp ile sipariş verin.`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: pkg.name,
          image: [origin + pkg.image],
          description: pkg.description,
          category: "Hazır Paket",
          brand: { "@type": "Brand", name: "Ertan Kasap" },
        }}
      />
      <section className="border-b border-dashed border-bordo/30">
        <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8">
          <Link
            to="/paketler"
            className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-charcoal/50 transition-colors hover:text-bordo"
          >
            <ChevronLeft className="h-4 w-4" /> Tüm Paketler
          </Link>
        </div>
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-8 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-12">
          <div className="relative overflow-hidden border-4 border-charcoal">
            <img src={pkg.image} alt={pkg.name} className="aspect-[4/3] w-full object-cover" />
            <span className="absolute left-0 top-5 inline-flex items-center gap-1.5 bg-bordo px-4 py-1.5 font-mono text-xs uppercase tracking-[0.15em] text-cream">
              <Users className="h-3.5 w-3.5" /> {pkg.serves}
            </span>
          </div>

          <div className="flex flex-col justify-center">
            <h1 className="font-serif text-5xl tracking-tight text-charcoal sm:text-6xl">{pkg.name}</h1>
            <p className="mt-4 text-lg leading-relaxed text-[#6b645c]">{pkg.description}</p>

            <div className="mt-7 border border-dashed border-bordo/30 bg-paper px-5 py-5">
              <div className="mb-3 flex items-center justify-between border-b border-dashed border-bordo/30 pb-2">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/60">Paket İçeriği</span>
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/60">Miktar</span>
              </div>
              <ul className="space-y-2.5">
                {pkg.items.map((it) => (
                  <li key={it.slug} className="flex items-center justify-between font-mono text-sm">
                    <Link
                      to={`/urun/${it.slug}`}
                      data-testid={`package-item-link-${it.slug}`}
                      className="inline-flex items-center gap-1 text-charcoal/85 transition-colors hover:text-bordo"
                    >
                      {it.name} <ArrowUpRight className="h-3 w-3 opacity-50" />
                    </Link>
                    <span className="font-bold text-bordo">{it.quantity}</span>
                  </li>
                ))}
              </ul>
            </div>

            <button
              data-testid="package-add-to-order"
              onClick={handleAdd}
              className="mt-6 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-sm bg-bordo px-5 py-3.5 text-sm font-bold uppercase tracking-wide text-cream transition-colors hover:bg-bordo-dark"
            >
              <Plus className="h-4 w-4" /> Tümünü Sipariş Listeme Ekle
            </button>
            <a
              href={askUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="package-whatsapp-ask"
              className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-sm border border-[#25D366] px-5 py-3.5 text-sm font-bold uppercase tracking-wide text-[#1a9e4b] transition-colors hover:bg-[#25D366] hover:text-charcoal"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp'tan Sor
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
